import { FocusEventHandler, Fragment, ReactElement, useMemo, useRef, useState } from 'react';

import { Combobox } from '@base-ui/react/combobox';

import { useAriaId } from '@components/component-helpers';
import { FormError } from '@components/form-error';
import { FieldDescription, FormLabel } from '@components/form-label';
import { IconProps } from '@components/icons';
import { isUserInputEdit } from '@components/select/base-ui-event';
import { createFilter } from '@components/select/filter';
import {
  highlightBehaviorProps,
  isSameOptionValue,
  optionLabelToString,
} from '@components/select/highlight';
import { useSelectTranslations } from '@components/select/locale';
import { NoOptions, OptionContent } from '@components/select/option-content';
import { ResultCount, usePickAnnouncement } from '@components/select/result-count';
import type { SelectOption as BaseSelectOption } from '@components/select/select';
import { Popup, Portal, Positioner, VirtualList } from '@components/select/styles';
import { OptionVirtualizer, scrollHighlightIntoView } from '@components/select/virtualized';
import { VirtualizedOptions } from '@components/select/virtualized-options';
import { Stack } from '@components/stack';
import { VisuallyHidden } from '@components/visually-hidden';
import { useEvent } from '@hooks/use-event';
import { useTrackMenuOpen } from '@stores/menu-open-store';
import { toArray } from '@utils/collections';

import { ChipsControl } from './chips-control';
import { useChipsPopup } from './use-chips-popup';

export type SelectOption = BaseSelectOption & { color?: string };

export interface SelectMultiProps {
  options: SelectOption[];
  value?: SelectOption[];
  defaultValue?: SelectOption | SelectOption[];
  onChange: (selectedOptions: SelectOption[]) => void;
  onBlur?: FocusEventHandler;
  label?: string;
  labelIcon?: ReactElement<IconProps>;
  labelInfo?: string;
  hideLabel?: boolean;
  description?: string;
  placeholder?: string;
  noOptions?: (obj: { inputValue: string }) => string | null;
  error?: string;
  menuPortalTarget?: HTMLElement;
  isDisabled?: boolean;
}

const optionFilter = createFilter<SelectOption>();

export function SelectMulti(props: SelectMultiProps) {
  const { t } = useSelectTranslations();
  const {
    options,
    value,
    defaultValue,
    onChange,
    onBlur,
    label,
    labelIcon,
    labelInfo,
    hideLabel = false,
    description,
    placeholder = t('placeholder'),
    noOptions = () => t('no_options'),
    error,
    menuPortalTarget,
    isDisabled = false,
  } = props;

  const selectId = useAriaId('selectmulti');
  const virtualizerRef = useRef<OptionVirtualizer | null>(null);
  const highlightedIndexRef = useRef(-1);
  const isControlled = value !== undefined;
  const [localSelected, setLocalSelected] = useState<SelectOption[]>(() => toArray(defaultValue));
  const selected = isControlled ? toArray(value) : localSelected;

  const { inputValue, setInputValue, isOpen, setIsOpen, handleOpenChange } = useChipsPopup();

  const pick = usePickAnnouncement(isOpen);

  const handleValueChange = useEvent((next: SelectOption[]) => {
    const added = next.find((option) => !selected.some((item) => item.value === option.value));
    const removed = selected.find((item) => !next.some((option) => option.value === item.value));
    if (!isControlled) setLocalSelected(next);
    // With a single option there is nothing left to pick after selecting it.
    if (added && options.length === 1) setIsOpen(false);
    onChange(next);
    if (added) pick.announcePick(optionLabelToString(added));
    if (removed) pick.announceRemoval(optionLabelToString(removed));
  });

  const handleInputValueChange = useEvent(
    (next: string, details: Combobox.Root.ChangeEventDetails) => {
      setInputValue(next);
      if (isUserInputEdit(details.reason)) {
        pick.clearPick();
        virtualizerRef.current?.scrollToOffset(0);
      }
    },
  );

  useTrackMenuOpen(isOpen);

  // Selected options render as chips in the control, so hide them from the
  // list itself.
  const visibleItems = useMemo(
    () => options.filter((option) => !selected.some((item) => item.value === option.value)),
    [options, selected],
  );

  const emptyMessage = noOptions({ inputValue });
  const LabelWrapper = hideLabel ? VisuallyHidden : Fragment;

  return (
    <Stack space={1}>
      <LabelWrapper>
        <FormLabel text={label} htmlFor={selectId} icon={labelIcon} info={labelInfo} />
      </LabelWrapper>
      <FieldDescription text={description} />
      <Combobox.Root<SelectOption, true>
        items={visibleItems}
        multiple
        value={selected}
        open={isOpen}
        onValueChange={handleValueChange}
        onOpenChange={handleOpenChange}
        inputValue={inputValue}
        onInputValueChange={handleInputValueChange}
        filter={optionFilter}
        isItemEqualToValue={isSameOptionValue}
        itemToStringLabel={optionLabelToString}
        disabled={isDisabled}
        virtualized
        onItemHighlighted={(_, { reason, index }) => {
          highlightedIndexRef.current = index;
          const virtualizer = virtualizerRef.current;
          if (virtualizer && reason === 'keyboard' && index >= 0) {
            scrollHighlightIntoView(virtualizer, index);
          }
        }}
        {...highlightBehaviorProps}
      >
        <ChipsControl
          inputId={selectId}
          selected={selected}
          placeholder={placeholder}
          isOpen={isOpen}
          onOpen={() => setIsOpen(true)}
          hasError={Boolean(error)}
          isDisabled={isDisabled}
          onBlur={onBlur}
        />
        <ResultCount
          isAnnouncing={isOpen}
          pickedLabel={pick.pickedLabel}
          removedLabel={pick.removedLabel}
        />

        <Portal container={menuPortalTarget}>
          <Positioner align="start">
            <Popup $isHiddenWhenEmpty={emptyMessage === null}>
              {emptyMessage !== null && <NoOptions>{emptyMessage}</NoOptions>}
              <VirtualList>
                <VirtualizedOptions
                  virtualizerRef={virtualizerRef}
                  highlightedIndexRef={highlightedIndexRef}
                  renderOption={(item: SelectOption) => (
                    <OptionContent
                      label={item.label}
                      helpText={item.helpText}
                      tooltip={item.tooltip}
                      hasReturnHint
                    />
                  )}
                />
              </VirtualList>
            </Popup>
          </Positioner>
        </Portal>
      </Combobox.Root>
      <FormError text={error} />
    </Stack>
  );
}
